import { gql, useQuery } from "@apollo/client";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IProductDetailQuery } from "@repo/shopify/queries/product-gql";
import type { ISearchEdge, ISearchQuery } from "@repo/shopify/queries/search-gql";
import { searchQuery } from "@repo/shopify/queries/search-gql";
import { getTotalProductCount } from "@repo/shopify/utils/collections";
import type { Product, ProductVariantConnection } from "@shopify/hydrogen-react/storefront-api-types";
import qs from "query-string";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Filter from "@molecules/Filter";
import ProductCard from "@molecules/ProductCard";

import { SEARCH_SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useHeaderScroll from "@hooks/useHeaderScroll";

import { useLayoutStore } from "@store/layout";

export default function SearchResult() {
  const navigate = useNavigate();
  const { search } = useLocation();

  const [searchParams] = useSearchParams();

  const { isPC } = useCheckDevice();

  const queryParameter = searchParams.get("q");

  useHeaderScroll();

  const queryString = useMemo(() => qs.parse(search), [search]);

  const { openToast, showFooter, setMeta, clearMeta, openSearch } = useLayoutStore();

  const [searchData, setSearchData] = useState<ISearchQuery["search"] | undefined>();
  const [products, setProducts] = useState<ISearchEdge[]>([]);
  const [expandProducts, setExpandProducts] = useState<ISearchEdge[]>([]);

  const [showFilter, setShowFilter] = useState<boolean>(false);

  useEffect(() => {
    if (!queryParameter) {
      navigate(URLS.HOME);
      setTimeout(() => {
        openSearch(true);
      }, 500);
    }

    showFooter(true);
  }, []);

  // 검색어 변경 될때 초기화 해줘야 함
  useEffect(() => {
    setExpandProducts([]);
    setSearchData(undefined);
    setShowFilter(false);
    window.scrollTo(0, 0);
  }, [queryParameter]);

  const variables = useMemo(() => {
    const sortQueries = SEARCH_SORT.find((item) => item.name === queryString.sort);

    const filterQueries = Object.entries(queryString)
      .filter(([key]) => key === "category" || key === "color" || key === "size")
      .map(([key, value]) => {
        const parseValue = JSON.parse((value as string) || "[]") as string[];
        return parseValue.map((item) =>
          key === "category" ? { productType: item } : { variantOption: { name: key, value: item } },
        );
      })
      .flat();

    return {
      first: queryString.view === "seperate" ? 12 : 24, // 쿼리 1회에 조회하는 product 수
      types: "PRODUCT",
      query: queryParameter,
      sortKey: sortQueries?.value ?? "RELEVANCE",
      reverse: sortQueries?.reverse ?? true,
      prefix: "LAST",
      productFilters: filterQueries || [],
    };
  }, [queryParameter, queryString]);

  const { loading, fetchMore } = useQuery<ISearchQuery>(gql(searchQuery.query), {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    onCompleted: (_data) => {
      if (!_data.search) {
        navigate(URLS.NOT_FOUND);
        return;
      }

      const searchList =
        (_data?.search?.edges as ISearchEdge[])
          ?.filter(({ node }) => Boolean(!(node as IProductDetailQuery["product"])?.vendor.includes("GWP")))
          ?.filter(({ node }) => (node as IProductDetailQuery["product"])?.hidden?.value !== "true") || [];

      setSearchData(_data?.search);
      setProducts(searchList);
      setExpandProducts(getExpandList(searchList));
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  const getExpandList = (searchList: ISearchEdge[]) => {
    const expandList: ISearchEdge[] = [];

    searchList.forEach(({ node }) => {
      const colorOptions = (node as Product).options.find((item) => item.name === "Color")?.values;

      if (colorOptions) {
        colorOptions.forEach((color) => {
          const colorList = (node as Product).variants.edges.filter(({ node: variantNode }) =>
            variantNode.selectedOptions.find((option) => option.value === color),
          );

          const newList: ISearchEdge = {
            node: {
              ...node,
              options: (node as Product).options.map((option) =>
                option.name === "Color" ? { ...option, values: [color] } : option,
              ),
              variants: { edges: colorList } as ProductVariantConnection,
            },
          } as ISearchEdge;

          expandList.push(newList);
        });
      }
    });

    return expandList;
  };

  const hasNextPage = useMemo(() => searchData?.pageInfo.hasNextPage || false, [searchData?.pageInfo]);
  const endCursor = useMemo(() => searchData?.pageInfo.endCursor || "", [searchData?.pageInfo]);

  const handleFetchMore = async () => {
    await fetchMore({
      variables: { ...variables, after: endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const seen = new Set<string>();
        const edges = [...prev.search.edges, ...fetchMoreResult.search.edges].filter(({ node }) => {
          const id = (node as ISearchEdge["node"])?.id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });

        return {
          search: {
            ...prev.search,
            edges,
            pageInfo: fetchMoreResult.search.pageInfo,
          },
        };
      },
    });
  };

  useEffect(() => {
    setMeta({ title: `Search Result for "${queryParameter}" - ${DEFAULT_TITLE}` });

    return () => {
      clearMeta();
    };
  }, [queryParameter]);

  const showProductList = useMemo(
    () => (queryString.view === "combined" ? products : expandProducts),
    [queryString, expandProducts, products],
  );

  const totalProducts = useMemo(() => getTotalProductCount(searchData?.productFilters || []), [searchData]);

  return (
    <>
      {!searchData && loading ? (
        <Loading type="page" />
      ) : (
        <>
          {isPC ? (
            <>
              <section className="pc:col-span-2 pc:gap-5 pc:w-screen-col-span-2 sticky top-30 left-5 col-span-8 flex max-h-dvh gap-2 self-start overflow-y-auto pb-46">
                {showFilter ? (
                  <Filter
                    filters={searchData?.productFilters || []}
                    onClickClose={() => {
                      setShowFilter(false);
                    }}
                  />
                ) : (
                  <div className="flex flex-col gap-10">
                    <Button
                      as="text"
                      className="justify-end"
                      onClick={() => {
                        setShowFilter(!showFilter);
                      }}
                    >
                      {`Filter & Sort (${showProductList.length > 0 ? totalProducts : 0})`}
                    </Button>
                  </div>
                )}
              </section>
              <section className="pc:col-span-10 pc:grid-cols-5 pc:col-start-3 col-span-8 flex flex-col gap-2.5">
                {showProductList.length ? (
                  <>
                    {isPC ? <h1 className="leading-none">{`Search Result for "${queryParameter}"`}</h1> : null}
                    <section className="pc:tnt-gap-x pc:col-span-10 pc:grid-cols-5 pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-5">
                      {showProductList.map(({ node }, index) => (
                        <ProductCard
                          color={node.options.find((item) => item.name === "Color")?.values || []}
                          key={`${node.id}_${index}`}
                          releaseDate={node?.release_date}
                          tags={node.tags}
                          title={node.title}
                          variants={node.variants}
                        />
                      ))}
                    </section>
                  </>
                ) : (
                  <>
                    {!loading ? (
                      <div className="pc:col-span-4 pc:gap-2.5 col-span-8 flex flex-col gap-2">
                        <div className="flex flex-col gap-1 leading-none">
                          {isPC ? <h1>{`Search Result for "${queryParameter}"`}</h1> : null}
                          <span>No results found for your search.</span>
                        </div>
                        <Link as="square" href={URLS.COLLECTIONS_ALL} variant="secondary">
                          View All Products
                        </Link>
                      </div>
                    ) : null}
                  </>
                )}

                {hasNextPage ? (
                  <Button
                    as="text"
                    className="pc:col-span-12 col-span-8 my-30 flex size-full justify-center"
                    loading={loading}
                    onClick={() => {
                      void handleFetchMore();
                    }}
                  >
                    {`Load More Products (${products.length} of ${totalProducts})`}
                  </Button>
                ) : (
                  <>
                    {showProductList.length > 20 ? (
                      <Button
                        as="text"
                        className="pc:col-span-12 col-span-8 mt-10 flex place-self-center"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Back to Top
                      </Button>
                    ) : null}
                  </>
                )}
              </section>
            </>
          ) : (
            <>
              {showFilter ? (
                <Filter
                  filters={searchData?.productFilters || []}
                  onClickClose={() => {
                    setShowFilter(false);
                  }}
                />
              ) : (
                <>
                  {showProductList.length > 0 && (
                    <div
                      className="sticky top-0 z-20 col-span-8 flex w-full justify-between gap-1 self-start bg-white py-2.5"
                      id="filter"
                    >
                      <h1 className="text-base! leading-none">{`Search Result for "${queryParameter}"`}</h1>
                      <Button
                        as="wrapper"
                        onClick={() => {
                          setShowFilter(true);
                        }}
                      >
                        <Icon name="Filter" />
                      </Button>
                    </div>
                  )}
                </>
              )}

              <section className="col-span-8 flex flex-col gap-2.5">
                {showProductList.length ? (
                  <>
                    {isPC ? <h1 className="leading-none">{`Search Result for "${queryParameter}"`}</h1> : null}
                    <section className="pc:tnt-gap-x pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-5">
                      {showProductList.map(({ node }, index) => (
                        <ProductCard
                          color={node.options.find((item) => item.name === "Color")?.values || []}
                          key={`${node.id}_${index}`}
                          releaseDate={node?.release_date}
                          tags={node.tags}
                          title={node.title}
                          variants={node.variants}
                        />
                      ))}
                    </section>
                  </>
                ) : (
                  <>
                    {!loading ? (
                      <div className="pc:gap-2.5 col-span-8 flex flex-col gap-4">
                        <div className="flex flex-col gap-1 text-base! leading-none">
                          <h1>{`Search Result for "${queryParameter}"`}</h1>
                          <span>No results found for your search.</span>
                        </div>
                        <Link as="square" href={URLS.COLLECTIONS_ALL} variant="secondary">
                          View All Products
                        </Link>
                      </div>
                    ) : null}
                  </>
                )}

                {hasNextPage ? (
                  <Button
                    as="text"
                    className="col-span-8 my-30 flex size-full justify-center"
                    loading={loading}
                    onClick={() => {
                      void handleFetchMore();
                    }}
                  >
                    {`Load More Products (${products.length} of ${totalProducts})`}
                  </Button>
                ) : (
                  <>
                    {showProductList.length > 20 ? (
                      <Button
                        as="text"
                        className="col-span-8 mt-10 flex place-self-center"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Back to Top
                      </Button>
                    ) : null}
                  </>
                )}
              </section>
            </>
          )}
        </>
      )}
    </>
  );
}
